import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { getSupabase } from '../lib/supabase';

/**
 * OAuth callback (E2.3). Kakao/Supabase redirect here with an authorization
 * code ( step 4). The Supabase SDK exchanges the code for a
 * session (PKCE), then we return the user home. Token storage is the SDK's job.
 */
export const Route = createFileRoute('/auth/callback')({
  // 로그인 화면 — 세션이 브라우저에만 있다. 서버는 레이아웃만 그린다(docs/SSR.md).
  ssr: false,
  component: AuthCallback,
});

function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // The PKCE code is single-use: exchanging it consumes the flow state. Guard so
  // StrictMode's double effect invocation (dev) doesn't fire a second exchange,
  // which would fail with flow_state_not_found.
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;

    // exchangeCodeForSession expects the bare authorization code, not the URL —
    // passing the full href sends it as auth_code and the server can't match the
    // flow state (flow_state_not_found).
    const code = new URLSearchParams(window.location.search).get('code');
    if (!code) {
      setError('로그인을 완료하지 못했어요. 다시 시도해 주세요.');
      return;
    }

    getSupabase().auth.exchangeCodeForSession(code).then(({ error: exchangeError }) => {
      if (exchangeError) {
        setError('로그인을 완료하지 못했어요. 다시 시도해 주세요.');
        return;
      }
      // AuthProvider picks up the new session via onAuthStateChange.
      navigate({ to: '/', replace: true });
    });
  }, [navigate]);

  return (
    <section
      aria-live="polite"
      className="flex min-h-[50vh] items-center justify-center px-4 text-center"
    >
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : (
        <p className="text-sm text-text-muted">로그인 처리 중…</p>
      )}
    </section>
  );
}
